// src/pages/workflow/WorkflowListPage.tsx
// Pipeline list — the main entry point for workflow automation.

import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflow, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Badge,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import {
  pipelineStatusMap,
  triggerTypeMap,
  filterPipelines,
  sortPipelinesByRecent,
  getEntityId,
  getPipelineName,
  formatRelativeTime,
  getStatusColor,
} from '@/features/workflow/logic';

// --- Inline SVG icons ---

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ImportIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  );
}

function TriggerIcon({ path, className }: { path: string; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
}

function GridIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}

function ListIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  );
}

function EmptyPipelineIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 64 48" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="24" r="3.5" />
      <path d="M8.5 24h5" />
      <path d="M13.5 24l5-5 5 5-5 5z" />
      <path d="M23.5 19l4-7" />
      <rect x="28" y="6" width="14" height="7" rx="1.5" />
      <path d="M23.5 29l4 7" />
      <rect x="28" y="35" width="14" height="7" rx="1.5" />
      <path d="M42 9.5h5v14.5M42 38.5h5V24" />
      <circle cx="55" cy="24" r="3" />
      <circle cx="55" cy="24" r="1.5" fill="currentColor" stroke="none" />
      <path d="M47 24h5" />
    </svg>
  );
}

// --- Toggle Switch ---

function ToggleSwitch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(!checked);
      }}
      className={`
        relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${checked ? 'bg-primary' : 'bg-muted-foreground/30'}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0
          transition duration-200 ease-in-out
          ${checked ? 'translate-x-4' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

// --- Mini Flow Visualization ---

function MiniFlowViz({ nodeCount, status }: { nodeCount: number; status: string }) {
  const count = Math.max(1, Math.min(nodeCount || 1, 8));
  const dotSize = 6;
  const gap = 20;
  const totalWidth = (count - 1) * gap + dotSize;
  const color = getStatusColor(status);

  // Generate slightly varied colors for nodes
  const nodeColors = [
    '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#6366f1', '#ec4899', '#14b8a6', '#f97316',
  ];

  return (
    <div className="flex items-center h-6 overflow-hidden">
      <svg width={Math.min(totalWidth + 4, 180)} height="16" className="flex-shrink-0">
        {Array.from({ length: count }).map((_, i) => {
          const x = i * gap + dotSize / 2 + 2;
          const nodeColor = i === 0 ? color : nodeColors[i % nodeColors.length];
          return (
            <g key={i}>
              {i < count - 1 && (
                <line
                  x1={x + dotSize / 2}
                  y1={8}
                  x2={x + gap - dotSize / 2}
                  y2={8}
                  stroke="var(--border)"
                  strokeWidth={1.5}
                />
              )}
              <circle
                cx={x}
                cy={8}
                r={dotSize / 2}
                fill={nodeColor}
                opacity={0.85}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// --- Toast ---

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 2500);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="flex items-center gap-2 bg-foreground text-background px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium">
        <svg className="h-4 w-4 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        {message}
      </div>
    </div>
  );
}

const STATUS_TABS = [
  { value: 'all', labelKey: 'common.all' },
  { value: 'PIPELINE_STATUS_ACTIVE', labelKey: 'workflow.status.active' },
  { value: 'PIPELINE_STATUS_DRAFT', labelKey: 'workflow.status.draft' },
  { value: 'PIPELINE_STATUS_PAUSED', labelKey: 'workflow.status.paused' },
];

export const WorkflowListPage = observer(() => {
  const store = useWorkflow();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [triggerFilter, setTriggerFilter] = useState(searchParams.get('trigger') || 'all');
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setIsLoading(true);
    store.ListPipelines()
      .then((res) => setPipelines(sortPipelinesByRecent(res?.items?.items || [])))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [searchParams]);

  const filtered = useMemo(
    () => filterPipelines(pipelines, search, statusFilter, triggerFilter),
    [pipelines, search, statusFilter, triggerFilter],
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: pipelines.length };
    for (const p of pipelines) {
      counts[p.status] = (counts[p.status] || 0) + 1;
    }
    return counts;
  }, [pipelines]);

  // --- Toggle active/inactive ---
  const handleToggleActive = useCallback(async (pipeline: any) => {
    const id = getEntityId(pipeline);
    const isActive = pipeline.status === 'PIPELINE_STATUS_ACTIVE';
    setTogglingIds((prev) => new Set(prev).add(id));
    try {
      if (isActive) {
        await store.PausePipeline?.(id, {});
      } else {
        await store.ResumePipeline?.(id, {});
      }
      setPipelines((prev) =>
        prev.map((p) =>
          getEntityId(p) === id
            ? { ...p, status: isActive ? 'PIPELINE_STATUS_PAUSED' : 'PIPELINE_STATUS_ACTIVE' }
            : p,
        ),
      );
    } catch {
      // Error handled by store
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [store]);

  // --- Quick run ---
  const handleQuickRun = useCallback(async (pipeline: any) => {
    const id = getEntityId(pipeline);
    try {
      await store.ExecutePipeline?.(id, { pipeline: { id } as any });
      setToastMessage(t('workflow.executionStarted'));
    } catch {
      // Error handled by store
    }
  }, [store, t]);

  // --- Batch selection ---
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((p: any) => getEntityId(p))));
    }
  }, [filtered, selectedIds.size]);

  const handleBatchActivate = useCallback(async () => {
    for (const id of selectedIds) {
      try { await store.ResumePipeline?.(id, {}); } catch {}
    }
    setSelectedIds(new Set());
    // Reload
    store.ListPipelines()
      .then((res) => setPipelines(sortPipelinesByRecent(res?.items?.items || [])))
      .catch(() => {});
  }, [selectedIds, store]);

  const handleBatchPause = useCallback(async () => {
    for (const id of selectedIds) {
      try { await store.PausePipeline?.(id, {}); } catch {}
    }
    setSelectedIds(new Set());
    store.ListPipelines()
      .then((res) => setPipelines(sortPipelinesByRecent(res?.items?.items || [])))
      .catch(() => {});
  }, [selectedIds, store]);

  const handleBatchDelete = useCallback(async () => {
    for (const id of selectedIds) {
      try { await store.DeletePipeline?.(id); } catch {}
    }
    setSelectedIds(new Set());
    store.ListPipelines()
      .then((res) => setPipelines(sortPipelinesByRecent(res?.items?.items || [])))
      .catch(() => {});
  }, [selectedIds, store]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('workflow.pipelines')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('workflow.pipelinesDesc')}</p>
        </div>
        <div className="flex gap-2">
          {/* View toggle */}
          <div className="flex items-center border border-border rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 transition-colors ${viewMode === 'grid' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              title={t('workflow.gridView')}
            >
              <GridIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-1.5 transition-colors ${viewMode === 'table' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              title={t('workflow.tableView')}
            >
              <ListIcon className="h-4 w-4" />
            </button>
          </div>
          <Link to="/workflows/templates">
            <Button variant="secondary" size="sm">
              <ImportIcon className="h-4 w-4 mr-1.5" />
              {t('workflow.templates')}
            </Button>
          </Link>
          <Link to="/workflows/new">
            <Button size="sm">
              <PlusIcon className="h-4 w-4 mr-1.5" />
              {t('workflow.createPipeline')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {STATUS_TABS.map((tab: { value: string; labelKey: string }) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`
              px-3 py-2 text-sm font-medium transition-colors relative
              ${statusFilter === tab.value
                ? 'text-foreground'
                : 'text-muted-foreground hover:text-foreground'
              }
            `}
          >
            {t(tab.labelKey)}
            {statusCounts[tab.value] !== undefined && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                {statusCounts[tab.value] || 0}
              </span>
            )}
            {statusFilter === tab.value && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />
            )}
          </button>
        ))}
      </div>

      {/* Search & filters */}
      <div className="flex gap-3">
        <form onSubmit={handleSearch} className="flex-1">
          <Input
            placeholder={t('workflow.searchPipelines')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-0"
          />
        </form>
        <Select
          value={triggerFilter}
          onChange={(e) => setTriggerFilter(e.target.value)} fullWidth={false} className="w-48 shrink-0">
          <option value="all">{t('workflow.allTriggers')}</option>
          <option value="TRIGGER_TYPE_MANUAL">{t('workflow.trigger.manual')}</option>
          <option value="TRIGGER_TYPE_SCHEDULE">{t('workflow.trigger.schedule')}</option>
          <option value="TRIGGER_TYPE_WEBHOOK">{t('workflow.trigger.webhook')}</option>
          <option value="TRIGGER_TYPE_EVENT">{t('workflow.trigger.event')}</option>
          <option value="TRIGGER_TYPE_FORM_SUBMISSION">{t('workflow.trigger.form')}</option>
          <option value="TRIGGER_TYPE_ENTITY_CHANGE">{t('workflow.trigger.entity')}</option>
        </Select>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Loading */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="animate-pulse space-y-3">
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-3 bg-muted rounded w-full" />
                  <div className="flex gap-2">
                    <div className="h-6 bg-muted rounded w-16" />
                    <div className="h-6 bg-muted rounded w-20" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <EmptyPipelineIcon className="h-16 w-16 text-muted-foreground/40 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">{t('workflow.noPipelines')}</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-md">
            {t('workflow.noPipelinesDesc')}
          </p>
          <div className="flex gap-3">
            <Link to="/workflows/new">
              <Button>{t('workflow.createPipeline')}</Button>
            </Link>
            <Link to="/workflows/templates">
              <Button variant="secondary">{t('workflow.browseTemplates')}</Button>
            </Link>
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        /* Pipeline grid (n8n style) */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((pipeline: any) => {
            const id = getEntityId(pipeline);
            const status = pipeline.status || 'PIPELINE_STATUS_DRAFT';
            const trigger = pipeline.trigger_type || 'TRIGGER_TYPE_MANUAL';
            const triggerInfo = triggerTypeMap[trigger] || triggerTypeMap.TRIGGER_TYPE_MANUAL;
            const isActive = status === 'PIPELINE_STATUS_ACTIVE';
            const isPaused = status === 'PIPELINE_STATUS_PAUSED';
            const canToggle = isActive || isPaused || status === 'PIPELINE_STATUS_DRAFT';
            const isSelected = selectedIds.has(id);
            const nodeCount = pipeline.node_count || pipeline.nodes?.length || 0;

            return (
              <div key={id} className="relative group">
                {/* Selection checkbox */}
                <div
                  className="absolute top-3 left-3 z-10"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(id)}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary/50 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity data-[selected=true]:opacity-100"
                      data-selected={isSelected || undefined}
                      style={isSelected ? { opacity: 1 } : undefined}
                    />
                  </label>
                </div>

                <Link to={`/workflows/${id}`} className="block">
                  <Card hoverable className={`h-full transition-all hover:border-primary/30 hover:shadow-md ${isSelected ? 'border-primary ring-1 ring-primary/20' : ''}`}>
                    <CardContent className="p-5">
                      {/* Top row: name + toggle + play */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0 pr-2">
                          <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                            {getPipelineName(pipeline)}
                          </h3>
                          {pipeline.description && (
                            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">
                              {pipeline.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.preventDefault()}>
                          {/* Quick run button (active pipelines only) */}
                          {isActive && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleQuickRun(pipeline);
                              }}
                              className="p-1 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                              title={t('workflow.runNow')}
                            >
                              <PlayIcon className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {/* Active/Inactive toggle */}
                          {canToggle && (
                            <ToggleSwitch
                              checked={isActive}
                              onChange={() => handleToggleActive(pipeline)}
                              disabled={togglingIds.has(id)}
                            />
                          )}
                        </div>
                      </div>

                      {/* Mini flow visualization */}
                      {nodeCount > 0 && (
                        <div className="mb-3">
                          <MiniFlowViz nodeCount={nodeCount} status={status} />
                        </div>
                      )}

                      {/* Trigger & category */}
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                          <TriggerIcon path={triggerInfo.icon} className="h-3.5 w-3.5" />
                          {t(triggerInfo.labelKey)}
                        </span>
                        {pipeline.category && (
                          <>
                            <span className="text-border">|</span>
                            <span>{pipeline.category}</span>
                          </>
                        )}
                      </div>

                      {/* Bottom stats bar (n8n style) */}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground pt-3 border-t border-border">
                        {nodeCount > 0 && (
                          <>
                            <span>{nodeCount} {t('workflow.nodes')}</span>
                            <span className="text-border/60">·</span>
                          </>
                        )}
                        {pipeline.last_executed_at ? (
                          <>
                            <span>{t('workflow.lastRun')} {formatRelativeTime(pipeline.last_executed_at)}</span>
                            <span className="text-border/60">·</span>
                          </>
                        ) : (
                          <>
                            <span>{t('workflow.neverRun')}</span>
                            <span className="text-border/60">·</span>
                          </>
                        )}
                        <span>{pipeline.execution_count || 0} {t('workflow.executions')}</span>
                      </div>

                      {/* Tags */}
                      {pipeline.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {pipeline.tags.slice(0, 3).map((tag: string) => (
                            <span
                              key={tag}
                              className="px-1.5 py-0.5 text-xs rounded bg-muted text-muted-foreground"
                            >
                              {tag}
                            </span>
                          ))}
                          {pipeline.tags.length > 3 && (
                            <span className="px-1.5 py-0.5 text-xs text-muted-foreground">
                              +{pipeline.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              </div>
            );
          })}
        </div>
      ) : (
        /* Table view */
        <Card>
          <CardContent className="p-0">
            <Table striped hoverable>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filtered.length && filtered.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary/50 cursor-pointer"
                    />
                  </TableHead>
                  <TableHead>{t('workflow.name')}</TableHead>
                  <TableHead className="w-28">{t('workflow.status')}</TableHead>
                  <TableHead className="w-36">{t('workflow.trigger')}</TableHead>
                  <TableHead className="w-20 text-center">{t('workflow.nodes')}</TableHead>
                  <TableHead className="w-32">{t('workflow.lastRun')}</TableHead>
                  <TableHead className="w-28 text-right">{t('workflow.executions')}</TableHead>
                  <TableHead className="w-32 text-right">{t('workflow.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableEmpty colSpan={8} message={t('workflow.noPipelines')} />
                ) : (
                  filtered.map((pipeline: any) => {
                    const id = getEntityId(pipeline);
                    const status = pipeline.status || 'PIPELINE_STATUS_DRAFT';
                    const statusInfo = pipelineStatusMap[status] || pipelineStatusMap.PIPELINE_STATUS_DRAFT;
                    const trigger = pipeline.trigger_type || 'TRIGGER_TYPE_MANUAL';
                    const triggerInfo = triggerTypeMap[trigger] || triggerTypeMap.TRIGGER_TYPE_MANUAL;
                    const isActive = status === 'PIPELINE_STATUS_ACTIVE';
                    const isPaused = status === 'PIPELINE_STATUS_PAUSED';
                    const canToggle = isActive || isPaused || status === 'PIPELINE_STATUS_DRAFT';
                    const isSelected = selectedIds.has(id);
                    const nodeCount = pipeline.node_count || pipeline.nodes?.length || 0;

                    return (
                      <TableRow key={id} className={isSelected ? 'bg-primary/5' : ''}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(id)}
                            className="h-4 w-4 rounded border-border text-primary focus:ring-primary/50 cursor-pointer"
                          />
                        </TableCell>
                        <TableCell>
                          <Link to={`/workflows/${id}`} className="hover:text-primary font-medium transition-colors">
                            {getPipelineName(pipeline)}
                          </Link>
                          {pipeline.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-xs mt-0.5">
                              {pipeline.description}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusInfo.variant as any} size="sm">
                            {t(statusInfo.labelKey)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <TriggerIcon path={triggerInfo.icon} className="h-3.5 w-3.5" />
                            {t(triggerInfo.labelKey)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center text-sm text-muted-foreground">
                          {nodeCount || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {pipeline.last_executed_at
                            ? formatRelativeTime(pipeline.last_executed_at)
                            : t('workflow.neverRun')}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {pipeline.execution_count || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {isActive && (
                              <button
                                onClick={() => handleQuickRun(pipeline)}
                                className="p-1 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                                title={t('workflow.runNow')}
                              >
                                <PlayIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {canToggle && (
                              <ToggleSwitch
                                checked={isActive}
                                onChange={() => handleToggleActive(pipeline)}
                                disabled={togglingIds.has(id)}
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Batch actions bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <div className="flex items-center gap-3 bg-foreground text-background px-5 py-3 rounded-xl shadow-2xl">
            <span className="text-sm font-medium">
              {selectedIds.size} {t('workflow.selected')}
            </span>
            <div className="w-px h-5 bg-background/20" />
            <button
              onClick={handleBatchActivate}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md hover:bg-background/10 transition-colors"
            >
              <PlayIcon className="h-3.5 w-3.5" />
              {t('workflow.activate')}
            </button>
            <button
              onClick={handleBatchPause}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md hover:bg-background/10 transition-colors"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
              </svg>
              {t('workflow.pause')}
            </button>
            <button
              onClick={handleBatchDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md text-red-400 hover:bg-background/10 transition-colors"
            >
              <TrashIcon className="h-3.5 w-3.5" />
              {t('workflow.delete')}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md hover:bg-background/10 transition-colors ml-1"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
});

export default WorkflowListPage;
